import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import "./ReviewPopup.css";

const ReviewPopupFrame = ({ title, onClose, busy = false, children }) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousPageOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onCloseRef.current();
      }

      if (event.key === "Tab") {
        const controls = [...dialogRef.current.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
        )].filter((element) => element.getClientRects().length > 0);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
        if (!controls.length) {
          event.preventDefault();
          dialogRef.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousPageOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <>
      <div className="review-popup-backdrop" onClick={() => {
        if (!busyRef.current) onCloseRef.current();
      }} aria-hidden="true" />
      <div className="review-popup-modal" ref={dialogRef} role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-busy={busy} tabIndex={-1}>
        <div className="review-popup-container">
          <div className="review-popup-header">
            <h2 className="review-popup-title" id={titleId}>{title}</h2>
            <button type="button" ref={closeRef} className="review-popup-close" aria-label="Close popup"
              onClick={onClose} disabled={busy}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="m18 6-12 12M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </>, document.body
  );
};

export default ReviewPopupFrame;
